/**
 * Bridge inside another site: a page puts `/embed` in an iframe (the snippet on the developers page) and the app
 * runs there without its header, footer and dots — just the tool, on a clear background — and tells the page how
 * tall it is, so the frame grows and shrinks with it (public/embed.js listens). The site may pick the colours with
 * `?theme=light|dark|auto`; the choice lasts for this frame's visit, through every page it opens.
 */

import { platform } from '@platform';

export const HEIGHT_MESSAGE = 'passwork-bridge:height';
const THEME_KEY = 'bridge.embed.theme';
const FILL_KEY = 'bridge.embed.fill';

type EmbedTheme = 'light' | 'dark' | 'auto';

/** True when the app runs inside another page's frame. */
export function isEmbedded(): boolean {
  return platform.isFramed();
}

function themeFrom(search: string): EmbedTheme | null {
  const value = new URLSearchParams(search).get('theme');
  return value === 'light' || value === 'dark' || value === 'auto' ? value : null;
}

function rememberedTheme(): EmbedTheme {
  try {
    const value = window.sessionStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * `?embed=1`: the frame belongs to the page that shows Bridge (Chat) and has a size of its own, so the tool is
 * centred in it with room all round instead of the frame being sized to the tool. Remembered for the visit.
 */
export function isFilled(search: string = window.location.search): boolean {
  if (!isEmbedded()) return false;
  const asked = new URLSearchParams(search).get('embed');
  try {
    if (asked === '1') window.sessionStorage.setItem(FILL_KEY, '1');
    return asked === '1' || window.sessionStorage.getItem(FILL_KEY) === '1';
  } catch {
    return asked === '1';
  }
}

/** Sets the frame up once, before the first paint: the marker for the styles, the colours, the height reports. */
export function installEmbed(): () => void {
  if (!isEmbedded()) return () => undefined;
  const root = document.documentElement;
  root.dataset.embed = isFilled() ? 'fill' : '';

  const asked = themeFrom(window.location.search);
  if (asked !== null) {
    try {
      window.sessionStorage.setItem(THEME_KEY, asked);
    } catch {
      // private mode: the choice holds for this page only
    }
  }
  const theme = asked ?? rememberedTheme();
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const paint = (): void => {
    root.dataset.theme = theme;
    root.dataset.system = mq?.matches === true ? 'dark' : 'light';
  };
  paint();
  mq?.addEventListener('change', paint);

  // the height of the content, sent whenever it changes; the page that embeds us sizes the frame to it
  let last = -1;
  const report = (): void => {
    const height = Math.ceil(document.documentElement.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    window.parent.postMessage({ type: HEIGHT_MESSAGE, height }, '*');
  };
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(report) : null;
  observer?.observe(document.documentElement);
  report();
  return () => {
    observer?.disconnect();
    mq?.removeEventListener('change', paint);
    delete root.dataset.embed;
  };
}
